import React, { useState, useEffect, useMemo, useCallback, useRef } from "react"
import {
  Table,
  Input,
  Tag,
  Avatar,
  Select,
  Pagination,
  Spin,
  message,
  Button,
} from "antd"
import {
  UnorderedListOutlined,
  AppstoreOutlined,
  SearchOutlined,
  FileExcelOutlined,
  DownloadOutlined,
  UploadOutlined,
} from "@ant-design/icons"
import { translations } from "../../../utils/Translation/staticTranslation"
import { Analytics } from "../../../Apis/analyticsApi"
import { Campaigns } from "../../../Apis/campaignsApi"
import UploadExcelModal from "./UploadExcelModal"
import { getCampaignPublicUrl } from "../../../utils/campaignsS3Upload"
import "../Campaigns.css"

const { Option } = Select

// Helper function to get country name from code
const getCountryName = (code, lang = "en") => {
  if (!code) return "—"
  try {
    const regionNames = new Intl.DisplayNames([lang === "ar" ? "ar" : "en"], {
      type: "region",
    })
    return regionNames.of(code.toUpperCase())
  } catch {
    return code.toUpperCase()
  }
}

const AudienceStep = ({
  formData,
  setFormData,
  viewMode = false,
  campaignId = null,
  autoSelectContactList = false,
  audiencePreset = null,
}) => {
  const lang = localStorage.getItem("language") || "ar"
  const t = translations[lang]?.campaignsPage
  // Initialize viewType:
  // - If autoSelectContactList is true (Custom Contact Users/Contact Groups with recipients) → contactList
  // - If audience is "Contact Users" (preset only, no recipients) → types
  // - Otherwise → types
  const [viewType, setViewType] = useState(() => {
    if (formData?.selectedContactGroups?.length > 0) return "contactList"
    if (formData?.selectedContactUsers?.length > 0) return "contactList"
    if (autoSelectContactList) return "contactList"
    if (formData?.selectedUsers?.length > 0) return "list"
    return "types"
  })
  const [contactListSubTab, setContactListSubTab] = useState(() => {
    if (formData?.selectedContactGroups?.length > 0) return "groups"
    if (audiencePreset === "Contact Groups" && autoSelectContactList) {
      return "groups"
    }
    return "users"
  })
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const [selectedContactListKeys, setSelectedContactListKeys] = useState([])
  const [selectedContactGroupKeys, setSelectedContactGroupKeys] = useState([])
  const [searchText, setSearchText] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [typeFilter, setTypeFilter] = useState(null)
  const [countryFilter, setCountryFilter] = useState(null)
  const [statusFilter, setStatusFilter] = useState(null)
  const [userData, setUserData] = useState([])
  const [loading, setLoading] = useState(false)
  const [availableCountries, setAvailableCountries] = useState([])
  const [contactListData, setContactListData] = useState([])
  const [contactListSearch, setContactListSearch] = useState("")
  const [debouncedContactListSearch, setDebouncedContactListSearch] = useState("")
  const [contactListLoading, setContactListLoading] = useState(false)
  const [contactListPagination, setContactListPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  })
  const [contactGroupsData, setContactGroupsData] = useState([])
  const [contactGroupsLoading, setContactGroupsLoading] = useState(false)
  const [contactGroupsPagination, setContactGroupsPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  })
  const [contactGroupsSearch, setContactGroupsSearch] = useState("")
  const [debouncedContactGroupsSearch, setDebouncedContactGroupsSearch] = useState("")
  const [audienceCounts, setAudienceCounts] = useState({
    all_users: 0,
    active_users: 0,
    inactive_users: 0,
    paid_users: 0,
    unpaid_users: 0,
    newsletter_users: 0,
    returning_users: 0,
    contact_users: 0,
  })
  const [loadingCounts, setLoadingCounts] = useState(false)
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  })
  const [showUploadExcelModal, setShowUploadExcelModal] = useState(false)

  // Helper function to normalize ID for comparison
  const normalizeId = (id) => {
    const numId = Number(id)
    return isNaN(numId) ? id : numId
  }

  // Helper function to check if ID exists in array (handles type mismatches)
  const idExists = (id, array) => {
    const normalized = normalizeId(id)
    return array.some((item) => normalizeId(item) === normalized)
  }

  const sortSelectedFirst = useCallback((rows, selectedKeys) => {
    if (!selectedKeys?.length) return rows
    return [...rows].sort((a, b) => {
      const aIsSelected = idExists(a.id, selectedKeys)
      const bIsSelected = idExists(b.id, selectedKeys)
      if (aIsSelected && !bIsSelected) return -1
      if (!aIsSelected && bIsSelected) return 1
      return 0
    })
  }, [])

  const audienceViewInitialized = useRef(false)

  useEffect(() => {
    audienceViewInitialized.current = false
  }, [campaignId])

  // Open the tab that matches saved audience when viewing/editing a campaign
  useEffect(() => {
    if (!campaignId || audienceViewInitialized.current) return

    const hasGroups = (formData.selectedContactGroups?.length ?? 0) > 0
    const hasContacts = (formData.selectedContactUsers?.length ?? 0) > 0
    const hasDashboard = (formData.selectedUsers?.length ?? 0) > 0
    const hasTypes = (formData.audiencePresets?.length ?? 0) > 0

    if (!hasGroups && !hasContacts && !hasDashboard && !hasTypes) return

    audienceViewInitialized.current = true

    if (hasGroups || hasContacts) {
      setViewType("contactList")
      setContactListSubTab(hasGroups ? "groups" : "users")
    } else if (hasDashboard) {
      setViewType("list")
    } else if (hasTypes) {
      setViewType("types")
    }
  }, [
    campaignId,
    formData.selectedContactGroups,
    formData.selectedContactUsers,
    formData.selectedUsers,
    formData.audiencePresets,
  ])

  const handleTypeToggle = (value) => {
    if (viewMode) return

    setFormData((prev) => {
      const current = prev.audiencePresets || []
      const isSelected = current.includes(value)
      const audiencePresets = isSelected
        ? current.filter((preset) => preset !== value)
        : [...current, value]

      return { ...prev, audiencePresets }
    })
  }

  const handleExcelUpload = (s3Key) => {
    setFormData((prev) => {
      // Delete / clear excel — keep any selected users / presets / contacts
      if (!s3Key) {
        const next = { ...prev, file_path: null }
        if (prev.audience === "Excel Sheet") {
          next.audience = ""
        }
        return next
      }

      // Upload adds excel without clearing multi-selected audience users
      return {
        ...prev,
        file_path: s3Key,
        // Keep legacy label only when no other audience is selected yet
        audience:
          (prev.selectedUsers?.length ?? 0) > 0 ||
          (prev.audiencePresets?.length ?? 0) > 0 ||
          (prev.selectedContactUsers?.length ?? 0) > 0 ||
          (prev.selectedContactGroups?.length ?? 0) > 0
            ? prev.audience === "Excel Sheet"
              ? ""
              : prev.audience
            : "Excel Sheet",
      }
    })
    setShowUploadExcelModal(false)
  }

  const handleDownloadExcel = () => {
    if (!formData.file_path) return

    try {
      // Download from S3
      const fileUrl = getCampaignPublicUrl(formData.file_path)
      
      const link = document.createElement("a")
      link.href = fileUrl
      link.download = formData.file_path.split("/").pop() || "campaign_excel.xlsx"
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      message.success(
        t?.audience?.excel_download_started || "Download started"
      )
    } catch (error) {
      console.error("Download error:", error)
      message.error(
        t?.audience?.failed_to_download_excel ||
          "Failed to download Excel file"
      )
    }
  }

  // Format number with commas
  const formatNumber = (num) => {
    return num?.toLocaleString() || "0"
  }

  const audienceOptions = [
    {
      label: t?.audience?.all_users || "All Users",
      value: "All Users",
      countKey: "all_users",
    },
    {
      label: t?.audience?.returning_users || "Returning Users",
      value: "Returning Users",
      countKey: "returning_users",
    },
    {
      label: t?.audience?.active_users || "Active Users",
      value: "Active Users",
      countKey: "active_users",
    },
    {
      label: t?.audience?.inactive_users || "Inactive Users",
      value: "Inactive Users",
      countKey: "inactive_users",
    },
    {
      label: t?.audience?.paid_users || "Paid Users",
      value: "Paid Users",
      countKey: "paid_users",
    },
    {
      label: t?.audience?.unpaid_users || "Unpaid Users",
      value: "Unpaid Users",
      countKey: "unpaid_users",
    },
    {
      label: t?.audience?.newsletter_users || "Newsletter Users",
      value: "Newsletter Users",
      countKey: "newsletter_users",
    },
    {
      label: t?.audience?.contact_users || "Contact Users",
      value: "Contact Users",
      countKey: "contact_users",
    },
  ]

  // Fetch user counts
  const fetchUsersCount = async () => {
    setLoadingCounts(true)
    try {
      const response = await Campaigns.getUsersCount()
      if (response.status === 200) {
        const result = response.data?.result || response.data
        if (result) {
          setAudienceCounts({
            all_users: result.all_users || 0,
            active_users: result.active_users || 0,
            inactive_users: result.inactive_users || 0,
            paid_users: result.paid_users || 0,
            unpaid_users: result.unpaid_users || 0,
            newsletter_users: result.newsletter_users || 0,
            returning_users: result.returning_users || 0,
            contact_users: result.contact_users || 0,
          })
        }
      }
    } catch (error) {
      console.error("Error fetching user counts:", error)
    } finally {
      setLoadingCounts(false)
    }
  }

  // Fetch contact groups list
  const fetchContactGroupsList = useCallback(async (page = 1, searchQuery = "") => {
    setContactGroupsLoading(true)
    try {
      const response = await Campaigns.getContactGroupsList({
        campaign_id: campaignId || undefined,
        contact_name: searchQuery || undefined,
        page: page,
        pageSize: contactGroupsPagination.pageSize,
      })

      if (response.status === 200) {
        const data = response.data || response
        const results = data.results || []

        setContactGroupsData(results)
        setContactGroupsPagination((prev) => ({
          ...prev,
          total: data.count || 0,
          current: page,
        }))
      }
    } catch (error) {
      console.error("Error fetching contact groups list:", error)
      message.error(
        t?.audience?.failed_to_fetch_contact_groups ||
          "Failed to fetch contact groups"
      )
      setContactGroupsData([])
    } finally {
      setContactGroupsLoading(false)
    }
  }, [campaignId, contactGroupsPagination.pageSize])

  // Fetch contact users list
  const fetchContactUsersList = useCallback(async (page = 1) => {
    setContactListLoading(true)
    try {
      const response = await Campaigns.getContactUsersList({
        contact_user_name: debouncedContactListSearch || undefined,
        campaign_id: campaignId || undefined,
        page: page,
        pageSize: contactListPagination.pageSize,
      })

      if (response.status === 200) {
        const data = response.data || response
        const results = data.results || []

        setContactListData(results)
        setContactListPagination((prev) => ({
          ...prev,
          total: data.count || 0,
          current: page,
        }))

        // Pre-select contact users based on formData.selectedContactUsers (from recipients)
        if (formData.selectedContactUsers && formData.selectedContactUsers.length > 0 && results.length > 0) {
          const normalizedSelectedIds = formData.selectedContactUsers.map((id) => normalizeId(id))
          const matchingKeys = results
            .filter((item) => normalizedSelectedIds.includes(normalizeId(item.id)))
            .map((item) => item.id)
          
          if (matchingKeys.length > 0) {
            // Merge with existing selections to handle pagination
            setSelectedContactListKeys((prev) => {
              const merged = [...new Set([...prev, ...matchingKeys])]
              return merged
            })
          }
        }
      }
    } catch (error) {
      console.error("Error fetching contact users list:", error)
      message.error(
        t?.audience?.failed_to_fetch_contact_users ||
          "Failed to fetch contact users"
      )
      setContactListData([])
    } finally {
      setContactListLoading(false)
    }
  }, [debouncedContactListSearch, campaignId, contactListPagination.pageSize])

  // Fetch counts on component mount
  useEffect(() => {
    fetchUsersCount()
  }, [])

  // Initialize selection from formData.selectedUsers when in edit/view mode (for regular list)
  useEffect(() => {
    if (viewMode && formData.selectedUsers && formData.selectedUsers.length > 0 && userData.length > 0) {
      const normalizedSelectedIds = formData.selectedUsers.map((id) => normalizeId(id))
      const matchingKeys = userData
        .filter((user) => normalizedSelectedIds.includes(normalizeId(user.id)))
        .map((user) => user.id)
      
      if (matchingKeys.length > 0) {
        setSelectedRowKeys((prev) => {
          const merged = [...new Set([...prev, ...matchingKeys])]
          if (merged.length !== prev.length || merged.some((key) => !prev.includes(key))) {
            return merged
          }
          return prev
        })
      }
    }
  }, [viewMode, formData.selectedUsers, userData])

  // Initialize selection from formData.selectedContactUsers when in edit/view mode
  useEffect(() => {
    if (
      (autoSelectContactList || viewMode) &&
      formData.selectedContactUsers &&
      formData.selectedContactUsers.length > 0
    ) {
      const normalizedIds = formData.selectedContactUsers.map((id) =>
        normalizeId(id)
      )

      setSelectedContactListKeys((prev) => {
        const merged = [...new Set([...prev, ...normalizedIds])]
        if (merged.length !== prev.length) {
          return merged
        }
        return prev
      })
    }
  }, [autoSelectContactList, viewMode, formData.selectedContactUsers])

  // Initialize selection from formData.selectedContactGroups when in edit/view mode
  useEffect(() => {
    if (
      (autoSelectContactList || viewMode) &&
      formData.selectedContactGroups &&
      formData.selectedContactGroups.length > 0
    ) {
      const normalizedIds = formData.selectedContactGroups.map((id) =>
        normalizeId(id)
      )

      setSelectedContactGroupKeys((prev) => {
        const merged = [...new Set([...prev, ...normalizedIds])]
        if (merged.length !== prev.length) {
          return merged
        }
        return prev
      })
    }
  }, [autoSelectContactList, viewMode, formData.selectedContactGroups])

  // Debounce contact list search
  useEffect(() => {
    if (viewType === "contactList") {
      const timer = setTimeout(() => setDebouncedContactListSearch(contactListSearch), 600)
      return () => clearTimeout(timer)
    }
  }, [contactListSearch, viewType])

  // Auto-select groups sub-tab when editing a campaign with group recipients (initial load only)
  useEffect(() => {
    if (audienceViewInitialized.current) return
    if (
      autoSelectContactList &&
      !viewMode &&
      formData.selectedContactGroups?.length > 0 &&
      viewType === "contactList" &&
      contactListSubTab !== "groups"
    ) {
      setContactListSubTab("groups")
    }
  }, [
    autoSelectContactList,
    viewMode,
    formData.selectedContactGroups,
    viewType,
    contactListSubTab,
  ])

  // Pre-select contact users when editing/viewing (initial selection when data loads)
  useEffect(() => {
    if (
      (autoSelectContactList || viewMode) &&
      formData.selectedContactUsers &&
      formData.selectedContactUsers.length > 0 &&
      contactListData.length > 0
    ) {
      const normalizedSelectedIds = formData.selectedContactUsers.map((id) => normalizeId(id))
      const matchingKeys = contactListData
        .filter((item) => normalizedSelectedIds.includes(normalizeId(item.id)))
        .map((item) => item.id)
      
      if (matchingKeys.length > 0) {
        // Merge with existing selections to handle pagination
        setSelectedContactListKeys((prev) => {
          const merged = [...new Set([...prev, ...matchingKeys])]
          // Only update if there are new keys to add
          if (merged.length !== prev.length || merged.some((key) => !prev.includes(key))) {
            return merged
          }
          return prev
        })
      }
    }
  }, [
    autoSelectContactList,
    viewMode,
    formData.selectedContactUsers,
    contactListData,
  ])

  // Pre-select contact groups when editing/viewing (initial selection when data loads)
  useEffect(() => {
    if (
      (autoSelectContactList || viewMode) &&
      formData.selectedContactGroups &&
      formData.selectedContactGroups.length > 0 &&
      contactGroupsData.length > 0
    ) {
      const normalizedSelectedIds = formData.selectedContactGroups.map((id) =>
        normalizeId(id)
      )
      const matchingKeys = contactGroupsData
        .filter((item) =>
          normalizedSelectedIds.includes(normalizeId(item.id))
        )
        .map((item) => item.id)

      if (matchingKeys.length > 0) {
        setSelectedContactGroupKeys((prev) => {
          const merged = [...new Set([...prev, ...matchingKeys])]
          if (
            merged.length !== prev.length ||
            merged.some((key) => !prev.includes(key))
          ) {
            return merged
          }
          return prev
        })
      }
    }
  }, [
    autoSelectContactList,
    viewMode,
    formData.selectedContactGroups,
    contactGroupsData,
  ])

  // Fetch contact list when view type changes or search changes
  useEffect(() => {
    if (viewType === "contactList" && contactListSubTab === "users") {
      setContactListPagination((prev) => ({ ...prev, current: 1 }))
      fetchContactUsersList(1)
    }
  }, [viewType, contactListSubTab, debouncedContactListSearch, campaignId, fetchContactUsersList])

  // Fetch contact groups when sub-tab changes to groups or search changes
  useEffect(() => {
    if (viewType === "contactList" && contactListSubTab === "groups") {
      setContactGroupsPagination((prev) => ({ ...prev, current: 1 }))
      fetchContactGroupsList(1, debouncedContactGroupsSearch)
    }
  }, [viewType, contactListSubTab, debouncedContactGroupsSearch, fetchContactGroupsList])

  // Debounce contact groups search
  useEffect(() => {
    if (viewType === "contactList" && contactListSubTab === "groups") {
      const timer = setTimeout(() => setDebouncedContactGroupsSearch(contactGroupsSearch), 600)
      return () => clearTimeout(timer)
    }
  }, [contactGroupsSearch, viewType, contactListSubTab])

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchText), 600)
    return () => clearTimeout(timer)
  }, [searchText])

  // Fetch users from API
  const fetchUsers = async (page = 1) => {
    setLoading(true)
    try {
      let res
      let responseData
      let results

      // If editing a campaign (campaignId exists), use campaign-specific API
      if (campaignId) {
        // Map status filter
        let apiStatus = ""
        if (statusFilter === "Active") {
          apiStatus = "active"
        } else if (statusFilter === "Inactive") {
          apiStatus = "inactive"
        }

        // Map plan filter
        let apiPlan = ""
        if (typeFilter) {
          // Map "Free Trial" to "free" for API compatibility
          if (typeFilter === "Free Trial") {
            apiPlan = "free"
          } else {
            apiPlan = typeFilter.toLowerCase()
          }
        }

        // Prepare filters object for campaign API
        const filters = {
          name: debouncedSearch,
          status: apiStatus,
          plan: apiPlan,
          country: countryFilter || "",
        }

        res = await Campaigns.getUserList(campaignId, page, filters)
        responseData = res?.data || res
        // Handle different response formats
        if (Array.isArray(responseData)) {
          results = responseData
        } else {
          results = responseData?.results || responseData?.data || []
        }
      } else {
        // Map status filter
        let apiStatus = ""
        if (statusFilter === "Active") {
          apiStatus = "active"
        } else if (statusFilter === "Inactive") {
          apiStatus = "inactive"
        }

        // Map plan filter
        let apiPlan = ""
        if (typeFilter) {
          // Map "Free Trial" to "free" for API compatibility
          if (typeFilter === "Free Trial") {
            apiPlan = "free"
          } else {
            apiPlan = typeFilter.toLowerCase()
          }
        }

        res = await Analytics.getUserList({
          search: debouncedSearch,
          status: apiStatus,
          plan: apiPlan,
          country: countryFilter || "",
          page,
        })

        responseData = res?.data || res
        results = responseData?.results || []
      }

      // Extract available countries dynamically
      if (responseData?.available_countries?.length) {
        const unique = [
          ...new Set(
            responseData.available_countries.map((c) => c.toUpperCase())
          ),
        ]
        setAvailableCountries(unique)
      }

      // Format user data
      const formatted = results.map((user) => {
        const code = user.country ? user.country.toUpperCase() : null
        // Ensure ID is a number for proper matching
        const userId = Number(user.user_id) || user.user_id
        return {
          key: userId,
          id: userId,
          name: user.name || "—",
          email: user.email || "—",
          country: getCountryName(code, lang),
          countryCode: code,
          plan: lang === "ar" ? user.plan_arabic : user.active_plan || "None",
          status: user.is_active ? "Active" : "Inactive",
          lastActivity:
            lang === "ar"
              ? user.last_activity_arabic || "—"
              : user.last_activity || "—",
        }
      })

      setUserData(formatted)

      // Pre-select users based on formData.selectedUsers (from recipients) in view/edit mode
      if (formData.selectedUsers && formData.selectedUsers.length > 0 && formatted.length > 0) {
        const normalizedSelectedIds = formData.selectedUsers.map((id) => normalizeId(id))
        const matchingKeys = formatted
          .filter((user) => normalizedSelectedIds.includes(normalizeId(user.id)))
          .map((user) => user.id)
        
        if (matchingKeys.length > 0) {
          // Merge with existing selections to handle pagination
          setSelectedRowKeys((prev) => {
            const merged = [...new Set([...prev, ...matchingKeys])]
            // Only update if there are new keys to add
            if (merged.length !== prev.length || merged.some((key) => !prev.includes(key))) {
              return merged
            }
            return prev
          })
        }
      }

      // Determine total count for pagination
      let totalCount = 0
      if (campaignId) {
        // For campaign API, try multiple possible count fields
        totalCount =
          responseData?.count ||
          responseData?.total ||
          responseData?.total_count ||
          0
        // If no count field found, but we have results, use results length as fallback
        // This is not ideal but better than showing 0
        if (totalCount === 0 && results.length > 0) {
          // If we got a full page (10 results), there might be more
          // Otherwise, this is likely the total
          totalCount = results.length < 10 ? results.length : results.length + 1
        }
      } else {
        totalCount = responseData?.count || 0
      }

      setPagination({
        current: page,
        pageSize: 10,
        total: totalCount,
      })
    } catch (err) {
      console.error("Error fetching users:", err)
      message.error(
        t?.audience?.failed_to_fetch_users ||
          "Failed to fetch users. Please try again."
      )
      setUserData([])
    } finally {
      setLoading(false)
    }
  }

  // Initialize selectedRowKeys from formData.selectedUsers when editing
  useEffect(() => {
    if (formData.selectedUsers && formData.selectedUsers.length > 0) {
      const keys = formData.selectedUsers.map((id) => normalizeId(id))
      setSelectedRowKeys(keys)
    } else {
      setSelectedRowKeys([])
    }
  }, [formData.selectedUsers])

  // Initialize selectedContactListKeys from formData.selectedContactUsers when editing
  useEffect(() => {
    if (formData.selectedContactUsers && formData.selectedContactUsers.length > 0) {
      const keys = formData.selectedContactUsers.map((id) => normalizeId(id))
      setSelectedContactListKeys(keys)
    } else {
      setSelectedContactListKeys([])
    }
  }, [formData.selectedContactUsers])

  // Initialize selectedContactGroupKeys from formData.selectedContactGroups when editing
  useEffect(() => {
    if (formData.selectedContactGroups && formData.selectedContactGroups.length > 0) {
      const keys = formData.selectedContactGroups.map((id) => normalizeId(id))
      setSelectedContactGroupKeys(keys)
    } else {
      setSelectedContactGroupKeys([])
    }
  }, [formData.selectedContactGroups])

  // Re-fetch when filters/search change or campaignId changes
  useEffect(() => {
    if (viewType === "list") {
      fetchUsers(1)
      setPagination((prev) => ({ ...prev, current: 1 }))
    }
  }, [
    debouncedSearch,
    typeFilter,
    countryFilter,
    statusFilter,
    viewType,
    campaignId,
  ])

  const getStatusClassName = (status) => {
    switch (status) {
      case "Active":
        return "status-tag status-tag-active"
      case "Inactive":
        return "status-tag status-tag-inactive"
      default:
        return "status-tag"
    }
  }

  const getPlanClassName = (plan) => {
    // Normalize Arabic to English for consistent color mapping
    const normalizedPlan =
      plan === "برو"
        ? "Pro"
        : plan === "بيسك"
        ? "Basic"
        : plan === "بزنس"
        ? "Business"
        : plan === "ستارتر"
        ? "Starter"
        : plan === "التجربة المجانية"
        ? "Free Trial"
        : plan === "التجربة المجانية"
        ? "Free Trial"
        : plan

    switch (normalizedPlan) {
      case "Pro":
        return "plan-tag plan-tag-pro"
      case "Starter":
        return "plan-tag plan-tag-starter"
      case "Basic":
        return "plan-tag plan-tag-basic"
      case "Business":
        return "plan-tag plan-tag-business"
      case "Free":
      case "Free Trial":
        return "plan-tag plan-tag-free"
      default:
        return "plan-tag"
    }
  }

  const columns = [
    {
      title: t?.audience?.table?.user || "User",
      dataIndex: "name",
      key: "user",
      render: (text, record) => {
        const avatarBgColor = getAvatarColor(text, record.id)
        const avatarTextColor = getAvatarTextColor(avatarBgColor)
        const initials = text
          ? text
              .split(" ")
              .map((n) => n[0])
              .join("")
              .slice(0, 2)
              .toUpperCase()
          : "U"

        return (
          <div className="user-cell">
            <Avatar
              src={record.avatar || null}
              className="audience-avatar"
              style={{
                backgroundColor: avatarBgColor,
                color: avatarTextColor,
              }}
            >
              {!record.avatar && initials}
            </Avatar>
            <div className="user-info">
              <div className="user-name">{text}</div>
              <div className="user-email">{record.email}</div>
            </div>
          </div>
        )
      },
    },
    {
      title: t?.audience?.table?.country || "Country",
      dataIndex: "country",
      key: "country",
      render: (text, record) => (
        <div className="country-cell">
          <span className="country-flag">
            {getCountryFlag(record.countryCode)}
          </span>
          <span>{text}</span>
        </div>
      ),
    },
    {
      title: t?.audience?.table?.status || "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag className={getStatusClassName(status)}>
          {status === "Active"
            ? t?.audience?.status?.active || "Active"
            : t?.audience?.status?.inactive || "Inactive"}
        </Tag>
      ),
    },
    {
      title: t?.audience?.table?.last_activity || "Last Activity",
      dataIndex: "lastActivity",
      key: "lastActivity",
    },
    {
      title: t?.audience?.table?.plan || "Plan",
      dataIndex: "plan",
      key: "plan",
      render: (plan) => <Tag className={getPlanClassName(plan)}>{plan}</Tag>,
    },
  ]

  const getCountryFlag = (code) => {
    if (!code) return "🌍"
    // Convert country code to flag emoji
    const codePoints = code
      .toUpperCase()
      .split("")
      .map((char) => 127397 + char.charCodeAt())
    return String.fromCodePoint(...codePoints)
  }

  // Generate consistent background color for avatar based on user name or ID
  const getAvatarColor = (name, id) => {
    const colors = [
      "#E0F7FA", // Light cyan
      "#F3E5F5", // Light purple
      "#E8F5E9", // Light green
      "#FFF3E0", // Light orange
      "#E1F5FE", // Light blue
      "#FCE4EC", // Light pink
      "#F1F8E9", // Light lime
      "#E0F2F1", // Light teal
      "#FFF8E1", // Light yellow
      "#EDE7F6", // Light deep purple
      "#E8EAF6", // Light indigo
      "#FFEBEE", // Light red
      "#E0F7FA", // Light cyan (duplicate for more variety)
      "#F3E5F5", // Light purple (duplicate)
    ]

    // Use name or ID to generate a consistent index
    const identifier = name || id || "default"
    let hash = 0
    for (let i = 0; i < identifier.length; i++) {
      hash = identifier.charCodeAt(i) + ((hash << 5) - hash)
    }
    const index = Math.abs(hash) % colors.length
    return colors[index]
  }

  // Get text color (dark) for better contrast
  const getAvatarTextColor = (bgColor) => {
    // Return dark color for light backgrounds
    return "#111"
  }

  const rowSelection = viewMode
    ? {
        selectedRowKeys,
        onChange: () => {
          // Disabled in view mode - no changes allowed
        },
        getCheckboxProps: (record) => ({
          disabled: true, // Disable checkboxes in view mode
          name: String(record.id),
        }),
        hideSelectAll: true, // Hide select all checkbox in view mode
      }
    : {
        selectedRowKeys,
        onChange: (keys) => {
          // Get current page user IDs (normalized)
          const currentPageIds = userData.map((user) => normalizeId(user.id))

          // Normalize keys to numbers for comparison
          const normalizedKeys = keys.map((key) => normalizeId(key))

          // Find which users were selected/deselected on current page
          const newlySelected = normalizedKeys.filter(
            (key) => !idExists(key, selectedRowKeys)
          )
          const newlyDeselected = currentPageIds.filter(
            (id) => !idExists(id, normalizedKeys)
          )

          // Merge: add newly selected, remove newly deselected
          let updatedKeys = [...selectedRowKeys]

          // Add newly selected keys
          newlySelected.forEach((key) => {
            if (!idExists(key, updatedKeys)) {
              updatedKeys.push(key)
            }
          })

          // Remove newly deselected keys
          updatedKeys = updatedKeys.filter(
            (key) => !idExists(key, newlyDeselected)
          )

          setSelectedRowKeys(updatedKeys)

          // Keep as numbers for API (recipients should be numeric IDs)
          const userIds = updatedKeys.map((key) => normalizeId(key))

          setFormData((prev) => ({
            ...prev,
            selectedUsers: userIds,
          }))
        },
        getCheckboxProps: (record) => ({
          // Ensure the checkbox works with numeric IDs
          name: String(record.id),
        }),
      }

  // Sort user data to show selected users at the top (current page only)
  const filteredData = useMemo(() => {
    return sortSelectedFirst(userData, selectedRowKeys)
  }, [userData, selectedRowKeys, sortSelectedFirst])

  const displayContactListData = useMemo(() => {
    return sortSelectedFirst(contactListData, selectedContactListKeys)
  }, [contactListData, selectedContactListKeys, sortSelectedFirst])

  const displayContactGroupsData = useMemo(() => {
    return sortSelectedFirst(contactGroupsData, selectedContactGroupKeys)
  }, [contactGroupsData, selectedContactGroupKeys, sortSelectedFirst])

  return (
    <div className="audience-step-container">
      <div className="audience-step-header">
        <h3 className="audience-step-title">
          {viewMode
            ? t?.audience?.target_audience || "Target Audience"
            : t?.audience?.select_target_audience || "Select Target Audience"}
        </h3>
        <div className="audience-header-actions">
          {/* In view mode show ONLY one download button (avoid duplicates) */}
          {!viewMode && formData.file_path && (
            <Button
              icon={<DownloadOutlined />}
              onClick={handleDownloadExcel}
              className="audience-download-excel-btn"
            >
              {t?.audience?.download_excel || "Download Excel"}
            </Button>
          )}
          {!viewMode && (
            <Button
              type="primary"
              icon={<FileExcelOutlined />}
              onClick={() => setShowUploadExcelModal(true)}
              className="audience-upload-excel-btn"
            >
              {formData.file_path
                ? t?.audience?.upload_new_excel || "Upload New Excel"
                : t?.audience?.upload_excel_sheet || "Upload Excel Sheet"}
            </Button>
          )}
          {viewMode && formData.file_path && (
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={handleDownloadExcel}
              className="audience-download-excel-btn-primary"
            >
              {t?.audience?.download_excel || "Download Excel"}
            </Button>
          )}
          <div className="audience-view-toggles">
            <button
              className={`view-toggle-btn ${viewType === "list" ? "active" : ""}`}
              onClick={() => setViewType("list")}
            >
              <UnorderedListOutlined />
              <span>{t?.audience?.list || "List"}</span>
            </button>
            <button
              className={`view-toggle-btn ${
                viewType === "types" ? "active" : ""
              }`}
              onClick={() => setViewType("types")}
            >
              <AppstoreOutlined />
              <span>{t?.audience?.types || "Types"}</span>
            </button>
            <button
              className={`view-toggle-btn ${
                viewType === "contactList" ? "active" : ""
              }`}
              onClick={() => setViewType("contactList")}
            >
              <UnorderedListOutlined />
              <span>{t?.audience?.contact_list || "Contact List"}</span>
            </button>
          </div>
        </div>
      </div>

      {viewType === "list" ? (
        <div className="audience-list-view">
          <div className="audience-table-container audience-table-wrapper">
            <div className="audience-filters-section audience-filters-section-wrapper">
              <div className="audience-filters-row">
                <Input
                  placeholder={t?.audience?.search || "Search"}
                  allowClear
                  prefix={<SearchOutlined className="audience-search-icon" />}
                  value={searchText}
                  onChange={(e) => !viewMode && setSearchText(e.target.value)}
                  className="audience-search-input"
                  disabled={viewMode}
                />
                <div className="audience-filters">
                  <Select
                    value={typeFilter}
                    onChange={setTypeFilter}
                    placeholder={t?.audience?.type || "Type"}
                    className="audience-filter-select audience-filter-select-inline"
                    allowClear
                    disabled={viewMode}
                  >
                    <Option value="Pro">{lang === "ar" ? "برو" : "Pro"}</Option>
                    <Option value="Starter">
                      {lang === "ar" ? "ستارتر" : "Starter"}
                    </Option>
                    <Option value="Basic">
                      {lang === "ar" ? "بيسك" : "Basic"}
                    </Option>
                    <Option value="Free Trial">
                      {lang === "ar" ? "التجربة المجانية" : "Free Trial"}
                    </Option>
                    <Option value="Business">
                      {lang === "ar" ? "أعمال" : "Business"}
                    </Option>
                  </Select>
                  <Select
                    value={countryFilter}
                    onChange={setCountryFilter}
                    placeholder={t?.audience?.country || "Country"}
                    className="audience-filter-select audience-filter-select-inline"
                    allowClear
                    disabled={viewMode}
                  >
                    {availableCountries.map((country) => (
                      <Option key={country} value={country}>
                        {country}
                      </Option>
                    ))}
                  </Select>
                  <Select
                    value={statusFilter}
                    onChange={setStatusFilter}
                    placeholder={t?.audience?.table?.status || "Status"}
                    className="audience-filter-select audience-filter-select-inline"
                    allowClear
                    disabled={viewMode}
                  >
                    <Option value="Active">
                      {t?.audience?.status?.active || "Active"}
                    </Option>
                    <Option value="Inactive">
                      {t?.audience?.status?.inactive || "Inactive"}
                    </Option>
                  </Select>
                </div>
              </div>
              <div className="audience-selection-summary">
                <span style={{ color: "#FB6514" }}>
                  {selectedRowKeys.length}
                </span>{" "}
                {t?.audience?.selected || "selected"}{" "}
                {t?.audience?.of || "of"}{" "}
                {pagination.total}{" "}
                {t?.audience?.available || "available"}
                {formData.file_path ? (
                  <span style={{ marginLeft: 8, color: "#308AA3" }}>
                    + {t?.audience?.excel_sheet || "Excel Sheet"}
                  </span>
                ) : null}
              </div>
            </div>
            <Spin spinning={loading}>
              <Table
                columns={columns}
                dataSource={filteredData}
                rowSelection={rowSelection}
                pagination={false}
                className="audience-table"
                rowKey="id"
                loading={loading}
              />
            </Spin>
          </div>
          <div className="audience-pagination-wrapper">
            <Pagination
              current={pagination.current}
              pageSize={pagination.pageSize}
              total={pagination.total}
              showSizeChanger={false}
              showLessItems={false}
              itemRender={(page, type, originalElement) => {
                const totalPages = Math.ceil(
                  pagination.total / pagination.pageSize
                )

                if (type === "prev") {
                  const isDisabled =
                    pagination.current === 1 || totalPages === 0

                  const handleClick = (e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    if (isDisabled) return

                    // Always go to previous page (same logic for both LTR and RTL)
                    if (pagination.current > 1) {
                      const newPage = pagination.current - 1
                      setPagination((prev) => ({ ...prev, current: newPage }))
                      fetchUsers(newPage)
                    }
                  }

                  return (
                    <a
                      onClick={handleClick}
                      onMouseDown={(e) => {
                        if (isDisabled) {
                          e.preventDefault()
                          e.stopPropagation()
                        }
                      }}
                      style={{
                        color: isDisabled ? "#D0D5DD" : "#18181B",
                        fontSize: "14px",
                        fontWeight: 400,
                        textDecoration: "none",
                        border: "none",
                        background: "transparent",
                        padding: 0,
                        cursor: isDisabled ? "not-allowed" : "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        pointerEvents: isDisabled ? "none" : "auto",
                        opacity: isDisabled ? 0.5 : 1,
                        userSelect: "none",
                      }}
                    >
                      {lang === "ar" ? (
                        <>
                          {t?.audience?.previous || "Previous"}
                          <img
                            src="/Campaign/ChevronRight.svg"
                            alt="Previous"
                            style={{
                              width: "16px",
                              height: "16px",
                              transform: "scaleX(-1)",
                            }}
                          />
                        </>
                      ) : (
                        <>
                          <img
                            src="/Campaign/ChevronLeft.svg"
                            alt="Previous"
                            style={{ width: "16px", height: "16px" }}
                          />
                          {t?.audience?.previous || "Previous"}
                        </>
                      )}
                    </a>
                  )
                }
                if (type === "next") {
                  const isDisabled =
                    pagination.current >= totalPages || totalPages === 0

                  const handleClick = (e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    if (isDisabled) return

                    // Always go to next page (same logic for both LTR and RTL)
                    if (pagination.current < totalPages) {
                      const newPage = pagination.current + 1
                      setPagination((prev) => ({ ...prev, current: newPage }))
                      fetchUsers(newPage)
                    }
                  }

                  return (
                    <a
                      onClick={handleClick}
                      onMouseDown={(e) => {
                        if (isDisabled) {
                          e.preventDefault()
                          e.stopPropagation()
                        }
                      }}
                      style={{
                        color: isDisabled ? "#D0D5DD" : "#18181B",
                        fontSize: "14px",
                        fontWeight: 400,
                        textDecoration: "none",
                        border: "none",
                        background: "transparent",
                        padding: 0,
                        cursor: isDisabled ? "not-allowed" : "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        pointerEvents: isDisabled ? "none" : "auto",
                        opacity: isDisabled ? 0.5 : 1,
                        userSelect: "none",
                      }}
                    >
                      {lang === "ar" ? (
                        <>
                          <img
                            src="/Campaign/ChevronLeft.svg"
                            alt="Next"
                            style={{
                              width: "16px",
                              height: "16px",
                              transform: "scaleX(-1)",
                            }}
                          />
                          {t?.audience?.next || "Next"}
                        </>
                      ) : (
                        <>
                          {t?.audience?.next || "Next"}
                          <img
                            src="/Campaign/ChevronRight.svg"
                            alt="Next"
                            style={{ width: "16px", height: "16px" }}
                          />
                        </>
                      )}
                    </a>
                  )
                }
                if (type === "page") {
                  const isActive = page === pagination.current
                  return (
                    <a
                      onClick={(e) => {
                        e.preventDefault()
                        setPagination({ ...pagination, current: page })
                        fetchUsers(page)
                      }}
                      style={{
                        color: "#18181B",
                        backgroundColor: isActive ? "#F9FAFB" : "transparent",
                        padding: isActive ? "8px 14px" : "8px 14px",
                        borderRadius: isActive ? "8px" : "8px",
                        fontSize: "14px",
                        fontWeight: 400,
                        border: isActive
                          ? "1px solid #D0D5DD"
                          : "1px solid transparent",
                        textDecoration: "none",
                        display: "inline-block",
                        textAlign: "center",
                        cursor: "pointer",
                        minWidth: "36px",
                      }}
                    >
                      {page}
                    </a>
                  )
                }
                if (type === "jump-prev" || type === "jump-next") {
                  // Calculate target page for ellipsis click
                  let targetPage
                  if (type === "jump-prev") {
                    targetPage = Math.max(1, pagination.current - 5)
                  } else {
                    targetPage = Math.min(totalPages, pagination.current + 5)
                  }

                  return (
                    <a
                      onClick={(e) => {
                        e.preventDefault()
                        setPagination({ ...pagination, current: targetPage })
                        fetchUsers(targetPage)
                      }}
                      style={{
                        color: "#18181B",
                        fontSize: "14px",
                        border: "none",
                        background: "transparent",
                        padding: "8px 14px",
                        cursor: "pointer",
                        display: "inline-block",
                        textAlign: "center",
                        minWidth: "36px",
                      }}
                    >
                      ...
                    </a>
                  )
                }
                return originalElement
              }}
              onChange={(page, pageSize) => {
                setPagination({ ...pagination, current: page, pageSize })
                fetchUsers(page)
              }}
            />
          </div>
        </div>
      ) : viewType === "contactList" ? (
        <div className="audience-list-view">
          <div className="audience-table-container audience-table-wrapper">
            <div className="audience-filters-section audience-filters-section-wrapper">
              <div className="audience-filters-row" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "16px" }}>
                <Input
                  placeholder={t?.audience?.search || "Search by name"}
                  allowClear
                  prefix={<SearchOutlined className="audience-search-icon" />}
                  value={contactListSubTab === "users" ? contactListSearch : contactGroupsSearch}
                  onChange={(e) => {
                    if (!viewMode) {
                      if (contactListSubTab === "users") {
                        setContactListSearch(e.target.value)
                      } else {
                        setContactGroupsSearch(e.target.value)
                      }
                    }
                  }}
                  className="audience-search-input"
                  disabled={viewMode}
                  style={{ maxWidth: 300 }}
                />
                <div style={{ display: "flex", gap: "8px" }}>
                  <button
                    className={`contact-list-subtab ${contactListSubTab === "users" ? "active" : ""}`}
                    onClick={() => setContactListSubTab("users")}
                    style={{
                      padding: "8px 16px",
                      border: "1px solid #E5E7EB",
                      borderRadius: "8px",
                      background: contactListSubTab === "users" ? "#504943" : "#fff",
                      color: contactListSubTab === "users" ? "#fff" : "#344054",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: 500,
                    }}
                  >
                    {t?.audience?.contact_list || "Contact List"}
                  </button>
                  <button
                    className={`contact-list-subtab ${contactListSubTab === "groups" ? "active" : ""}`}
                    onClick={() => setContactListSubTab("groups")}
                    style={{
                      padding: "8px 16px",
                      border: "1px solid #E5E7EB",
                      borderRadius: "8px",
                      background: contactListSubTab === "groups" ? "#504943" : "#fff",
                      color: contactListSubTab === "groups" ? "#fff" : "#344054",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: 500,
                    }}
                  >
                    {t?.audience?.contact_groups || "Contact Groups"}
                  </button>
                </div>
              </div>
            </div>
            {contactListSubTab === "users" ? (
              <>
                <Spin spinning={contactListLoading}>
                  <Table
                    columns={[
                      {
                        title: t?.audience?.table?.name || "Name",
                        dataIndex: "name",
                        key: "name",
                      },
                      {
                        title: t?.audience?.table?.email || "Email",
                        dataIndex: "email",
                        key: "email",
                      },
                    ]}
                    dataSource={displayContactListData}
                    rowSelection={
                      viewMode
                        ? {
                            selectedRowKeys: selectedContactListKeys,
                            onChange: () => {
                              // Disabled in view mode - no changes allowed
                            },
                            getCheckboxProps: (record) => ({
                              disabled: true, // Disable checkboxes in view mode
                              name: String(record.id),
                            }),
                            hideSelectAll: true, // Hide select all checkbox in view mode
                          }
                        : {
                            selectedRowKeys: selectedContactListKeys,
                            onChange: (selectedKeys) => {
                              // Get current page user IDs (normalized)
                              const currentPageIds = contactListData.map((user) => normalizeId(user.id))

                              // Normalize keys to numbers for comparison
                              const normalizedKeys = selectedKeys.map((key) => normalizeId(key))

                              // Find which users were selected/deselected on current page
                              const newlySelected = normalizedKeys.filter(
                                (key) => !idExists(key, selectedContactListKeys)
                              )
                              const newlyDeselected = currentPageIds.filter(
                                (id) => !idExists(id, normalizedKeys)
                              )

                              // Merge: add newly selected, remove newly deselected
                              let updatedKeys = [...selectedContactListKeys]

                              // Add newly selected keys
                              newlySelected.forEach((key) => {
                                if (!idExists(key, updatedKeys)) {
                                  updatedKeys.push(key)
                                }
                              })

                              // Remove newly deselected keys
                              updatedKeys = updatedKeys.filter(
                                (key) => !idExists(key, newlyDeselected)
                              )

                              setSelectedContactListKeys(updatedKeys)
                              
                              const userIds = updatedKeys.map((key) => normalizeId(key))
                              setFormData((prev) => ({
                                ...prev,
                                selectedContactUsers: userIds,
                              }))
                            },
                            getCheckboxProps: (record) => ({
                              name: String(record.id),
                            }),
                          }
                    }
                    pagination={false}
                    className="audience-table"
                    rowKey="id"
                    loading={contactListLoading}
                  />
                </Spin>
                <div className="audience-pagination-wrapper">
                  <Pagination
                    current={contactListPagination.current}
                    pageSize={contactListPagination.pageSize}
                    total={contactListPagination.total}
                    showSizeChanger={false}
                    showLessItems={false}
                    itemRender={(page, type, originalElement) => {
                      const totalPages = Math.ceil(
                        contactListPagination.total / contactListPagination.pageSize
                      )

                      if (type === "prev") {
                        const isDisabled =
                          contactListPagination.current === 1 || totalPages === 0

                        const handleClick = (e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          if (isDisabled) return

                          if (contactListPagination.current > 1) {
                            const newPage = contactListPagination.current - 1
                            setContactListPagination((prev) => ({ ...prev, current: newPage }))
                            fetchContactUsersList(newPage)
                          }
                        }

                        return (
                          <a
                            onClick={handleClick}
                            onMouseDown={(e) => {
                              if (isDisabled) {
                                e.preventDefault()
                                e.stopPropagation()
                              }
                            }}
                            style={{
                              color: isDisabled ? "#D0D5DD" : "#18181B",
                              fontSize: "14px",
                              fontWeight: 400,
                              textDecoration: "none",
                              border: "none",
                              background: "transparent",
                              padding: 0,
                              cursor: isDisabled ? "not-allowed" : "pointer",
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                              pointerEvents: isDisabled ? "none" : "auto",
                              opacity: isDisabled ? 0.5 : 1,
                              userSelect: "none",
                            }}
                          >
                            {lang === "ar" ? (
                              <>
                                {t?.audience?.previous || "Previous"}
                                <img
                                  src="/Campaign/ChevronRight.svg"
                                  alt="Previous"
                                  style={{
                                    width: "16px",
                                    height: "16px",
                                    transform: "scaleX(-1)",
                                  }}
                                />
                              </>
                            ) : (
                              <>
                                <img
                                  src="/Campaign/ChevronLeft.svg"
                                  alt="Previous"
                                  style={{ width: "16px", height: "16px" }}
                                />
                                {t?.audience?.previous || "Previous"}
                              </>
                            )}
                          </a>
                        )
                      }
                      if (type === "next") {
                        const isDisabled =
                          contactListPagination.current >= totalPages || totalPages === 0

                        const handleClick = (e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          if (isDisabled) return

                          if (contactListPagination.current < totalPages) {
                            const newPage = contactListPagination.current + 1
                            setContactListPagination((prev) => ({ ...prev, current: newPage }))
                            fetchContactUsersList(newPage)
                          }
                        }

                        return (
                          <a
                            onClick={handleClick}
                            onMouseDown={(e) => {
                              if (isDisabled) {
                                e.preventDefault()
                                e.stopPropagation()
                              }
                            }}
                            style={{
                              color: isDisabled ? "#D0D5DD" : "#18181B",
                              fontSize: "14px",
                              fontWeight: 400,
                              textDecoration: "none",
                              border: "none",
                              background: "transparent",
                              padding: 0,
                              cursor: isDisabled ? "not-allowed" : "pointer",
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                              pointerEvents: isDisabled ? "none" : "auto",
                              opacity: isDisabled ? 0.5 : 1,
                              userSelect: "none",
                            }}
                          >
                            {lang === "ar" ? (
                              <>
                                <img
                                  src="/Campaign/ChevronLeft.svg"
                                  alt="Next"
                                  style={{
                                    width: "16px",
                                    height: "16px",
                                    transform: "scaleX(-1)",
                                  }}
                                />
                                {t?.audience?.next || "Next"}
                              </>
                            ) : (
                              <>
                                {t?.audience?.next || "Next"}
                                <img
                                  src="/Campaign/ChevronRight.svg"
                                  alt="Next"
                                  style={{ width: "16px", height: "16px" }}
                                />
                              </>
                            )}
                          </a>
                        )
                      }
                      if (type === "page") {
                        const isActive = page === contactListPagination.current
                        return (
                          <a
                            onClick={(e) => {
                              e.preventDefault()
                              setContactListPagination((prev) => ({ ...prev, current: page }))
                              fetchContactUsersList(page)
                            }}
                            style={{
                              color: "#18181B",
                              backgroundColor: isActive ? "#F9FAFB" : "transparent",
                              padding: isActive ? "8px 14px" : "8px 14px",
                              borderRadius: isActive ? "8px" : "8px",
                              fontSize: "14px",
                              fontWeight: 400,
                              border: isActive
                                ? "1px solid #D0D5DD"
                                : "1px solid transparent",
                              textDecoration: "none",
                              display: "inline-block",
                              textAlign: "center",
                              cursor: "pointer",
                              minWidth: "36px",
                            }}
                          >
                            {page}
                          </a>
                        )
                      }
                      if (type === "jump-prev" || type === "jump-next") {
                        let targetPage
                        if (type === "jump-prev") {
                          targetPage = Math.max(1, contactListPagination.current - 5)
                        } else {
                          targetPage = Math.min(totalPages, contactListPagination.current + 5)
                        }

                        return (
                          <a
                            onClick={(e) => {
                              e.preventDefault()
                              setContactListPagination((prev) => ({ ...prev, current: targetPage }))
                              fetchContactUsersList(targetPage)
                            }}
                            style={{
                              color: "#18181B",
                              fontSize: "14px",
                              border: "none",
                              background: "transparent",
                              padding: "8px 14px",
                              cursor: "pointer",
                              display: "inline-block",
                              textAlign: "center",
                              minWidth: "36px",
                            }}
                          >
                            ...
                          </a>
                        )
                      }
                      return originalElement
                    }}
                    onChange={(page, pageSize) => {
                      setContactListPagination({ ...contactListPagination, current: page, pageSize })
                      fetchContactUsersList(page)
                    }}
                  />
                </div>
              </>
            ) : (
              <>
                <Spin spinning={contactGroupsLoading}>
                  <Table
                    columns={[
                      {
                        title: t?.audience?.table?.name || "Name",
                        dataIndex: "name",
                        key: "name",
                      },
                      {
                        title: t?.audience?.table?.description || "Description",
                        dataIndex: "description",
                        key: "description",
                        render: (text) => text || "—",
                      },
                      {
                        title: t?.audience?.total_contact_users || "Total Contact Users",
                        dataIndex: "total_contact_users",
                        key: "total_contact_users",
                        render: (count) => count || 0,
                      },
                    ]}
                    dataSource={displayContactGroupsData}
                    rowSelection={
                      viewMode
                        ? {
                            selectedRowKeys: selectedContactGroupKeys,
                            onChange: () => {
                              // Disabled in view mode - no changes allowed
                            },
                            getCheckboxProps: (record) => ({
                              disabled: true, // Disable checkboxes in view mode
                              name: String(record.id),
                            }),
                            hideSelectAll: true, // Hide select all checkbox in view mode
                          }
                        : {
                            selectedRowKeys: selectedContactGroupKeys,
                            onChange: (selectedKeys) => {
                              // Get current page group IDs (normalized)
                              const currentPageIds = contactGroupsData
                                .filter((group) => 
                                  !searchText || group.name?.toLowerCase().includes(searchText.toLowerCase())
                                )
                                .map((group) => normalizeId(group.id))

                              // Normalize keys to numbers for comparison
                              const normalizedKeys = selectedKeys.map((key) => normalizeId(key))

                              // Find which groups were selected/deselected on current page
                              const newlySelected = normalizedKeys.filter(
                                (key) => !idExists(key, selectedContactGroupKeys)
                              )
                              const newlyDeselected = currentPageIds.filter(
                                (id) => !idExists(id, normalizedKeys)
                              )

                              // Merge: add newly selected, remove newly deselected
                              let updatedKeys = [...selectedContactGroupKeys]

                              // Add newly selected keys
                              newlySelected.forEach((key) => {
                                if (!idExists(key, updatedKeys)) {
                                  updatedKeys.push(key)
                                }
                              })

                              // Remove newly deselected keys
                              updatedKeys = updatedKeys.filter(
                                (key) => !idExists(key, newlyDeselected)
                              )

                              setSelectedContactGroupKeys(updatedKeys)
                              
                              const groupIds = updatedKeys.map((key) => normalizeId(key))
                              setFormData((prev) => ({
                                ...prev,
                                selectedContactGroups: groupIds,
                              }))
                            },
                            getCheckboxProps: (record) => ({
                              name: String(record.id),
                            }),
                          }
                    }
                    pagination={false}
                    className="audience-table"
                    rowKey="id"
                    loading={contactGroupsLoading}
                  />
                </Spin>
                <div className="audience-pagination-wrapper">
                  <Pagination
                    current={contactGroupsPagination.current}
                    pageSize={contactGroupsPagination.pageSize}
                    total={contactGroupsPagination.total}
                    showSizeChanger={false}
                    showLessItems={false}
                    itemRender={(page, type, originalElement) => {
                      const totalPages = Math.ceil(
                        contactGroupsPagination.total / contactGroupsPagination.pageSize
                      )

                      if (type === "prev") {
                        const isDisabled =
                          contactGroupsPagination.current === 1 || totalPages === 0

                        const handleClick = (e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          if (isDisabled) return

                          if (contactGroupsPagination.current > 1) {
                            const newPage = contactGroupsPagination.current - 1
                            setContactGroupsPagination((prev) => ({ ...prev, current: newPage }))
                            fetchContactGroupsList(newPage, debouncedContactGroupsSearch)
                          }
                        }

                        return (
                          <a
                            onClick={handleClick}
                            onMouseDown={(e) => {
                              if (isDisabled) {
                                e.preventDefault()
                                e.stopPropagation()
                              }
                            }}
                            style={{
                              color: isDisabled ? "#D0D5DD" : "#18181B",
                              fontSize: "14px",
                              fontWeight: 400,
                              textDecoration: "none",
                              border: "none",
                              background: "transparent",
                              padding: 0,
                              cursor: isDisabled ? "not-allowed" : "pointer",
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                              pointerEvents: isDisabled ? "none" : "auto",
                              opacity: isDisabled ? 0.5 : 1,
                              userSelect: "none",
                            }}
                          >
                            {lang === "ar" ? (
                              <>
                                {t?.audience?.previous || "Previous"}
                                <img
                                  src="/Campaign/ChevronRight.svg"
                                  alt="Previous"
                                  style={{
                                    width: "16px",
                                    height: "16px",
                                    transform: "scaleX(-1)",
                                  }}
                                />
                              </>
                            ) : (
                              <>
                                <img
                                  src="/Campaign/ChevronLeft.svg"
                                  alt="Previous"
                                  style={{ width: "16px", height: "16px" }}
                                />
                                {t?.audience?.previous || "Previous"}
                              </>
                            )}
                          </a>
                        )
                      }
                      if (type === "next") {
                        const isDisabled =
                          contactGroupsPagination.current >= totalPages || totalPages === 0

                        const handleClick = (e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          if (isDisabled) return

                          if (contactGroupsPagination.current < totalPages) {
                            const newPage = contactGroupsPagination.current + 1
                            setContactGroupsPagination((prev) => ({ ...prev, current: newPage }))
                            fetchContactGroupsList(newPage, debouncedContactGroupsSearch)
                          }
                        }

                        return (
                          <a
                            onClick={handleClick}
                            onMouseDown={(e) => {
                              if (isDisabled) {
                                e.preventDefault()
                                e.stopPropagation()
                              }
                            }}
                            style={{
                              color: isDisabled ? "#D0D5DD" : "#18181B",
                              fontSize: "14px",
                              fontWeight: 400,
                              textDecoration: "none",
                              border: "none",
                              background: "transparent",
                              padding: 0,
                              cursor: isDisabled ? "not-allowed" : "pointer",
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                              pointerEvents: isDisabled ? "none" : "auto",
                              opacity: isDisabled ? 0.5 : 1,
                              userSelect: "none",
                            }}
                          >
                            {lang === "ar" ? (
                              <>
                                <img
                                  src="/Campaign/ChevronLeft.svg"
                                  alt="Next"
                                  style={{
                                    width: "16px",
                                    height: "16px",
                                    transform: "scaleX(-1)",
                                  }}
                                />
                                {t?.audience?.next || "Next"}
                              </>
                            ) : (
                              <>
                                {t?.audience?.next || "Next"}
                                <img
                                  src="/Campaign/ChevronRight.svg"
                                  alt="Next"
                                  style={{ width: "16px", height: "16px" }}
                                />
                              </>
                            )}
                          </a>
                        )
                      }
                      if (type === "page") {
                        const isActive = page === contactGroupsPagination.current
                        return (
                          <a
                            onClick={(e) => {
                              e.preventDefault()
                              setContactGroupsPagination((prev) => ({ ...prev, current: page }))
                              fetchContactGroupsList(page, debouncedContactGroupsSearch)
                            }}
                            style={{
                              color: "#18181B",
                              backgroundColor: isActive ? "#F9FAFB" : "transparent",
                              padding: isActive ? "8px 14px" : "8px 14px",
                              borderRadius: isActive ? "8px" : "8px",
                              fontSize: "14px",
                              fontWeight: 400,
                              border: isActive
                                ? "1px solid #D0D5DD"
                                : "1px solid transparent",
                              textDecoration: "none",
                              display: "inline-block",
                              textAlign: "center",
                              cursor: "pointer",
                              minWidth: "36px",
                            }}
                          >
                            {page}
                          </a>
                        )
                      }
                      if (type === "jump-prev" || type === "jump-next") {
                        let targetPage
                        if (type === "jump-prev") {
                          targetPage = Math.max(1, contactGroupsPagination.current - 5)
                        } else {
                          targetPage = Math.min(totalPages, contactGroupsPagination.current + 5)
                        }

                        return (
                          <a
                            onClick={(e) => {
                              e.preventDefault()
                              setContactGroupsPagination((prev) => ({ ...prev, current: targetPage }))
                              fetchContactGroupsList(targetPage, debouncedContactGroupsSearch)
                            }}
                            style={{
                              color: "#18181B",
                              fontSize: "14px",
                              border: "none",
                              background: "transparent",
                              padding: "8px 14px",
                              cursor: "pointer",
                              display: "inline-block",
                              textAlign: "center",
                              minWidth: "36px",
                            }}
                          >
                            ...
                          </a>
                        )
                      }
                      return originalElement
                    }}
                    onChange={(page, pageSize) => {
                      setContactGroupsPagination({ ...contactGroupsPagination, current: page, pageSize })
                      fetchContactGroupsList(page, debouncedContactGroupsSearch)
                    }}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      ) : (
        <div className="audience-cards-grid">
          {audienceOptions.map((option) => {
            const count = audienceCounts[option.countKey] || 0
            return (
              <div
                key={option.value}
                className={`audience-card ${
                  formData.audiencePresets?.includes(option.value)
                    ? "selected"
                    : ""
                } ${viewMode ? "disabled" : ""}`}
                onClick={() => !viewMode && handleTypeToggle(option.value)}
                style={viewMode ? { cursor: "not-allowed", opacity: 0.6 } : {}}
              >
                <div className="audience-card-label">{option.label}</div>
                <div className="audience-card-count">
                  {loadingCounts
                    ? t?.audience?.loading || "Loading..."
                    : `${formatNumber(count)} ${t?.audience?.users || "users"}`}
                </div>
              </div>
            )
          })}
        </div>
      )}

      <UploadExcelModal
        visible={showUploadExcelModal}
        onClose={() => setShowUploadExcelModal(false)}
        onUpload={handleExcelUpload}
        existingFile={formData.file_path}
        viewMode={viewMode}
        campaignId={campaignId}
      />
    </div>
  )
}

export default AudienceStep
